import type { TopProduct, TopProductsPeriod } from '@/api/models';

/**
 * Iconify identifiers for the medal shown on the top three ranks. Ranks
 * outside this map render as a plain numeric badge in the View.
 */
export const TOP_PRODUCTS_MEDAL_ICON: Record<1 | 2 | 3, string> = {
    1: 'solar:cup-star-bold-duotone',
    2: 'solar:medal-star-bold-duotone',
    3: 'solar:medal-ribbons-star-bold-duotone',
};

/**
 * Solar token class for each medal so the gold/silver/bronze hue stays in one
 * place. The class targets `color` on the rendered SVG.
 */
export const TOP_PRODUCTS_MEDAL_COLOR_CLASS: Record<1 | 2 | 3, string> = {
    1: 'text-warning',
    2: 'text-default-500',
    3: 'text-warning-soft-foreground',
};

/**
 * i18n key (relative to the `pages` namespace) for each supported period.
 * Keyed by the wire value (kebab-case) so the server's response can be used
 * to look up the localized label without a normalization step.
 */
export const TOP_PRODUCTS_PERIOD_LABEL_KEY: Record<TopProductsPeriod, string> =
    {
        'this-month': 'sales.widgets.topProducts.thisMonth',
    };

/**
 * Narrowing guard used by the View to decide between the medal icon and the
 * numeric badge fallback. Kept here so the rendering branch in the View has
 * no business rule of its own.
 */
export const isMedalRank = (rank: number): rank is 1 | 2 | 3 => {
    return rank === 1 || rank === 2 || rank === 3;
};

/**
 * Share of the top performer's revenue, expressed as 0..100 for the progress
 * bar. The leader always renders at 100%; a zero or missing leader collapses
 * the bar to 0 instead of producing `NaN`/`Infinity`.
 */
export const getShareOfTop = (
    revenue: number,
    leaderRevenue: number,
): number => {
    if (leaderRevenue <= 0) {
        return 0;
    }
    const share = (revenue / leaderRevenue) * 100;
    return Math.max(0, Math.min(100, share));
};

/**
 * Revenue of the highest-ranked item, used as the denominator for
 * `getShareOfTop`. Falls back to `0` for an empty list so callers can guard
 * with the same `leaderRevenue <= 0` check above.
 */
export const getLeaderRevenue = (items: TopProduct[]): number => {
    return items.reduce((max, item) => Math.max(max, item.revenue), 0);
};
